import { useState } from 'react';
import {
  BarChart3,
  TrendingUp,
  TrendingDown,
  Users,
  Eye,
  Clock,
  Globe,
  Monitor,
  Smartphone,
  Tablet,
  ArrowUpRight,
  Calendar,
} from 'lucide-react';
import { cn } from '@/lib/cn';

interface StatCard {
  label: string;
  value: string;
  change: number;
  icon: React.ReactNode;
}

interface PageView {
  page: string;
  views: number;
  uniqueVisitors: number;
  avgTime: string;
  bounceRate: number;
}

interface TrafficSource {
  source: string;
  visitors: number;
  percentage: number;
  color: string;
}

interface CountryData {
  country: string;
  flag: string;
  visitors: number;
  percentage: number;
}

// Demo data
const stats: StatCard[] = [
  { label: 'Toplam Ziyaretci', value: '45,234', change: 12.5, icon: <Users size={24} /> },
  { label: 'Sayfa Goruntulenme', value: '128,543', change: 8.2, icon: <Eye size={24} /> },
  { label: 'Ort. Oturum Suresi', value: '3m 42s', change: -2.1, icon: <Clock size={24} /> },
  { label: 'Hemen Cikma Orani', value: '%34.2', change: -5.3, icon: <ArrowUpRight size={24} /> },
];

const pageViews: PageView[] = [
  { page: '/', views: 32456, uniqueVisitors: 28234, avgTime: '2m 15s', bounceRate: 32 },
  { page: '/products', views: 18234, uniqueVisitors: 15432, avgTime: '4m 30s', bounceRate: 24 },
  { page: '/services', views: 12543, uniqueVisitors: 10234, avgTime: '3m 45s', bounceRate: 28 },
  { page: '/blog', views: 9876, uniqueVisitors: 8234, avgTime: '5m 12s', bounceRate: 18 },
  { page: '/contact', views: 7654, uniqueVisitors: 6543, avgTime: '1m 30s', bounceRate: 45 },
  { page: '/about', views: 5432, uniqueVisitors: 4321, avgTime: '2m 00s', bounceRate: 38 },
];

const trafficSources: TrafficSource[] = [
  { source: 'Organik Arama', visitors: 18234, percentage: 40, color: 'bg-green-500' },
  { source: 'Direkt', visitors: 13675, percentage: 30, color: 'bg-blue-500' },
  { source: 'Sosyal Medya', visitors: 9117, percentage: 20, color: 'bg-purple-500' },
  { source: 'Referans', visitors: 4559, percentage: 10, color: 'bg-orange-500' },
];

const countries: CountryData[] = [
  { country: 'Turkiye', flag: '🇹🇷', visitors: 28234, percentage: 62 },
  { country: 'Almanya', flag: '🇩🇪', visitors: 5432, percentage: 12 },
  { country: 'ABD', flag: '🇺🇸', visitors: 4321, percentage: 10 },
  { country: 'Ingiltere', flag: '🇬🇧', visitors: 3210, percentage: 7 },
  { country: 'Fransa', flag: '🇫🇷', visitors: 2109, percentage: 5 },
  { country: 'Diger', flag: '🌍', visitors: 1928, percentage: 4 },
];

const devices = [
  { type: 'Masaustu', icon: <Monitor size={20} />, percentage: 58, color: 'text-blue-500' },
  { type: 'Mobil', icon: <Smartphone size={20} />, percentage: 35, color: 'text-green-500' },
  { type: 'Tablet', icon: <Tablet size={20} />, percentage: 7, color: 'text-purple-500' },
];

// Simple bar chart data (last 7 days)
const weeklyData = [
  { day: 'Pzt', visitors: 4234 },
  { day: 'Sal', visitors: 5123 },
  { day: 'Car', visitors: 4876 },
  { day: 'Per', visitors: 6234 },
  { day: 'Cum', visitors: 7123 },
  { day: 'Cmt', visitors: 5432 },
  { day: 'Paz', visitors: 4123 },
];

const maxVisitors = Math.max(...weeklyData.map(d => d.visitors));

export function AdminAnalytics() {
  const [dateRange, setDateRange] = useState('7d');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-text-primary">Analitik</h1>
          <p className="text-text-secondary">Site trafigi ve kullanici davranislari</p>
        </div>
        <div className="flex items-center gap-2">
          <Calendar size={18} className="text-text-tertiary" />
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="bg-bg-elevated border border-border-default rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-accent"
          >
            <option value="24h">Son 24 Saat</option>
            <option value="7d">Son 7 Gun</option>
            <option value="30d">Son 30 Gun</option>
            <option value="90d">Son 90 Gun</option>
          </select>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat, index) => (
          <div
            key={index}
            className="bg-bg-card border border-border-default rounded-xl p-5"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="p-2 rounded-lg bg-accent/10 text-accent">
                {stat.icon}
              </div>
              <div className={cn(
                'flex items-center gap-1 text-xs px-2 py-1 rounded-full',
                stat.change >= 0 ? 'bg-green-500/10 text-green-500' : 'bg-red-500/10 text-red-500'
              )}>
                {stat.change >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                %{Math.abs(stat.change)}
              </div>
            </div>
            <p className="text-2xl font-bold text-text-primary mb-1">{stat.value}</p>
            <p className="text-sm text-text-secondary">{stat.label}</p>
          </div>
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Weekly Traffic Chart */}
        <div className="lg:col-span-2 bg-bg-card border border-border-default rounded-xl p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-text-primary">Haftalik Trafik</h2>
            <BarChart3 size={20} className="text-text-tertiary" />
          </div>
          <div className="flex items-end justify-between h-48 gap-2">
            {weeklyData.map((data, index) => (
              <div key={index} className="flex-1 flex flex-col items-center gap-2">
                <div className="w-full bg-bg-elevated rounded-t-lg relative" style={{ height: '180px' }}>
                  <div
                    className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-accent to-accent/50 rounded-t-lg transition-all duration-300"
                    style={{ height: `${(data.visitors / maxVisitors) * 100}%` }}
                  />
                </div>
                <span className="text-xs text-text-tertiary">{data.day}</span>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-4 border-t border-border-default flex items-center justify-between text-sm">
            <span className="text-text-secondary">Toplam: <span className="text-text-primary font-semibold">37,145</span> ziyaretci</span>
            <span className="text-green-500">+12.5% onceki haftaya gore</span>
          </div>
        </div>

        {/* Device Distribution */}
        <div className="bg-bg-card border border-border-default rounded-xl p-6">
          <h2 className="text-lg font-semibold text-text-primary mb-6">Cihaz Dagilimi</h2>
          <div className="space-y-4">
            {devices.map((device, index) => (
              <div key={index} className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className={device.color}>{device.icon}</span>
                    <span className="text-sm text-text-primary">{device.type}</span>
                  </div>
                  <span className="text-sm font-semibold text-text-primary">%{device.percentage}</span>
                </div>
                <div className="w-full h-2 bg-bg-elevated rounded-full overflow-hidden">
                  <div
                    className={cn('h-full rounded-full', device.color.replace('text-', 'bg-'))}
                    style={{ width: `${device.percentage}%` }}
                  />
                </div>
              </div>
            ))}
          </div>

          {/* Mini Stats */}
          <div className="mt-6 pt-6 border-t border-border-default grid grid-cols-2 gap-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-text-primary">1920</p>
              <p className="text-xs text-text-tertiary">Ort. Ekran Genisligi</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-text-primary">Chrome</p>
              <p className="text-xs text-text-tertiary">En Populer Tarayici</p>
            </div>
          </div>
        </div>
      </div>

      {/* Traffic Sources & Countries */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Traffic Sources */}
        <div className="bg-bg-card border border-border-default rounded-xl p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-text-primary">Trafik Kaynaklari</h2>
            <Globe size={20} className="text-text-tertiary" />
          </div>
          <div className="space-y-4">
            {trafficSources.map((source, index) => (
              <div key={index} className="flex items-center gap-4">
                <div className={cn('w-3 h-3 rounded-full', source.color)} />
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-text-primary">{source.source}</span>
                    <span className="text-sm text-text-secondary">{source.visitors.toLocaleString()}</span>
                  </div>
                  <div className="w-full h-1.5 bg-bg-elevated rounded-full overflow-hidden">
                    <div
                      className={cn('h-full rounded-full', source.color)}
                      style={{ width: `${source.percentage}%` }}
                    />
                  </div>
                </div>
                <span className="text-sm font-semibold text-text-primary w-12 text-right">%{source.percentage}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Countries */}
        <div className="bg-bg-card border border-border-default rounded-xl p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-text-primary">Ulkelere Gore Ziyaretciler</h2>
          </div>
          <div className="space-y-3">
            {countries.map((country, index) => (
              <div key={index} className="flex items-center gap-3 p-2 rounded-lg hover:bg-bg-elevated transition-colors">
                <span className="text-2xl">{country.flag}</span>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-text-primary">{country.country}</span>
                    <span className="text-sm text-text-secondary">{country.visitors.toLocaleString()}</span>
                  </div>
                  <div className="w-full h-1 bg-bg-elevated rounded-full overflow-hidden mt-1">
                    <div
                      className="h-full bg-accent rounded-full"
                      style={{ width: `${country.percentage}%` }}
                    />
                  </div>
                </div>
                <span className="text-xs text-text-tertiary w-10 text-right">%{country.percentage}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Page Views Table */}
      <div className="bg-bg-card border border-border-default rounded-xl overflow-hidden">
        <div className="p-6 border-b border-border-default">
          <h2 className="text-lg font-semibold text-text-primary">Sayfa Performansi</h2>
          <p className="text-sm text-text-secondary">En cok ziyaret edilen sayfalar</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-bg-elevated">
              <tr>
                <th className="text-left px-6 py-3 text-xs font-semibold text-text-tertiary uppercase tracking-wider">Sayfa</th>
                <th className="text-right px-6 py-3 text-xs font-semibold text-text-tertiary uppercase tracking-wider">Goruntulenme</th>
                <th className="text-right px-6 py-3 text-xs font-semibold text-text-tertiary uppercase tracking-wider">Tekil Ziyaretci</th>
                <th className="text-right px-6 py-3 text-xs font-semibold text-text-tertiary uppercase tracking-wider">Ort. Sure</th>
                <th className="text-right px-6 py-3 text-xs font-semibold text-text-tertiary uppercase tracking-wider">Hemen Cikma</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border-default">
              {pageViews.map((page, index) => (
                <tr key={index} className="hover:bg-bg-elevated/50 transition-colors">
                  <td className="px-6 py-4">
                    <span className="text-sm font-medium text-accent">{page.page}</span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <span className="text-sm text-text-primary">{page.views.toLocaleString()}</span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <span className="text-sm text-text-secondary">{page.uniqueVisitors.toLocaleString()}</span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <span className="text-sm text-text-secondary">{page.avgTime}</span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <span className={cn(
                      'text-sm',
                      page.bounceRate < 30 ? 'text-green-500' : page.bounceRate < 40 ? 'text-yellow-500' : 'text-red-500'
                    )}>
                      %{page.bounceRate}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
